import { useState, useEffect } from "react";
import { ShoppingBag, Menu as MenuIcon, X, Globe, Phone } from "lucide-react";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { HoursRibbon } from "./HoursRibbon";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const { lang, setLang, cartCount, setCartOpen } = useApp();
  const t = useTranslation(lang);
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(true);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 0);
    // Keep glass state active from the initial paint (no need to scroll first).
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "home", label: t.nav.home },
    { id: "menu", label: t.nav.menu },
    { id: "official-menu", label: t.nav.official },
    { id: "reviews", label: t.nav.reviews },
    { id: "gallery", label: t.nav.gallery },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <nav
      data-testid="navbar"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 glass-nav"
    >
      <HoursRibbon lang={lang} />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
        <button
          data-testid="logo-btn"
          onClick={() => scrollTo("home")}
          className="flex items-center gap-2 group"
        >
          <span className="text-2xl md:text-3xl font-serif-q5 text-gold tracking-wider">Q5</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-[0.3em] text-neutral-400 group-hover:text-gold transition-colors">
            Restaurante
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-link-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-[13px] uppercase tracking-[0.18em] text-neutral-300 hover:text-gold transition-colors duration-300"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-3 md:gap-4">
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="lang-toggle"
              className="flex items-center gap-1.5 text-[12px] uppercase tracking-[0.2em] text-neutral-300 hover:text-gold transition-colors"
            >
              <Globe size={15} />
              {lang.toUpperCase()}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#141414] border-[#2A2A2A] text-neutral-200 min-w-[120px]">
              <DropdownMenuItem data-testid="lang-es" onClick={() => setLang("es")} className="cursor-pointer focus:bg-[#A91D11]/20 focus:text-white">
                Español
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="lang-en" onClick={() => setLang("en")} className="cursor-pointer focus:bg-[#A91D11]/20 focus:text-white">
                English
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            data-testid="cart-open-btn"
            onClick={() => setCartOpen(true)}
            className="relative flex items-center gap-2 text-neutral-200 hover:text-gold transition-colors"
            aria-label={t.nav.cart}
          >
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -top-2 -right-2 bg-[#A91D11] text-white text-[10px] font-semibold h-5 min-w-5 px-1 rounded-full flex items-center justify-center"
              >
                {cartCount}
              </span>
            )}
          </button>

          <a
            data-testid="nav-call-btn"
            href={`tel:${PHONES[0].tel}`}
            className="hidden md:inline-flex btn-primary-q5 items-center gap-2"
          >
            <Phone size={14} /> {t.nav.call}
          </a>

          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-neutral-200"
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden glass-nav border-t border-white/10 mt-3">
          <ul className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <li key={l.id}>
                <button
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-sm uppercase tracking-[0.2em] text-neutral-200 hover:text-gold"
                >
                  {l.label}
                </button>
              </li>
            ))}
            <li>
              <a
                data-testid="nav-mobile-call"
                href={`tel:${PHONES[0].tel}`}
                className="btn-primary-q5 w-full"
              >
                <Phone size={14} /> {t.nav.call}
              </a>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
};
